import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Check, Plus, Trash2, Download, Users, BookOpen, Image as ImageIcon, Share2, Copy, MessageCircle, Send, Mail, Link } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth config with token from localStorage
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  };
};

// Hardcoded sample songs for users to choose from
const SAMPLE_SONGS = [
  { id: 1, title: 'Janji Suci - Yovie & Nuno', url: 'https://www.youtube.com/watch?v=NLjmAgPGVXE' },
  { id: 2, title: 'Kau Ilhamku - Man Bai', url: 'https://www.youtube.com/watch?v=3mCQxzZP8Oo' },
  { id: 3, title: 'Sampai Jannah - Wali', url: 'https://www.youtube.com/watch?v=E5yFcdNdJp4' },
  { id: 4, title: 'Cinta Sejati - BCL', url: 'https://www.youtube.com/watch?v=Oz2gfP-nkXg' },
  { id: 5, title: 'Perfect - Ed Sheeran', url: 'https://www.youtube.com/watch?v=2Vv-BfVoq4g' },
  { id: 6, title: 'A Thousand Years - Christina Perri', url: 'https://www.youtube.com/watch?v=rtOvBOTyX00' },
];

// Font family mapping
const FONT_FAMILIES = {
  'playfair': "'Playfair Display', serif",
  'greatvibes': "'Great Vibes', cursive",
  'dancing': "'Dancing Script', cursive",
  'pinyon': "'Pinyon Script', cursive",
  'tangerine': "'Tangerine', cursive",
  'alex': "'Alex Brush', cursive",
  'allura': "'Allura', cursive",
  'sacramento': "'Sacramento', cursive",
  'cormorant': "'Cormorant Garamond', serif",
  'cinzel': "'Cinzel', serif",
  'manrope': "'Manrope', sans-serif",
  'lora': "'Lora', serif",
  'italiana': "'Italiana', serif",
};

// Default card colors
const DEFAULT_CARD_COLORS = {
  text_primary: "#FFFFFF",
  text_secondary: "#FFFFFF",
  accent_color: "#D97706",
  countdown_bg: "rgba(255,255,255,0.1)",
  countdown_text: "#FFFFFF",
  overlay_opacity: 0.3,
  onboarding_button_bg: "#FFFFFF",
  onboarding_button_text: "#000000",
  button_bg: "#064E3B",
  button_text: "#FFFFFF"
};

export default function EditorPage() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [uploadingCouple, setUploadingCouple] = useState(false);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [rsvpList, setRsvpList] = useState([]);
  const [guestbookList, setGuestbookList] = useState([]);
  const [defaultDoas, setDefaultDoas] = useState([]);
  const [activeTab, setActiveTab] = useState('onboarding');
  const [user, setUser] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingPreview, setSavingPreview] = useState(false);
  const [capturingThumbnail, setCapturingThumbnail] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [gallerySongs, setGallerySongs] = useState([]);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [audioSource, setAudioSource] = useState('gallery'); // 'gallery' or 'custom'
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const qrInputRef = useRef(null);
  const coupleInputRef = useRef(null);
  const wishlistImageRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    fetchCard();
    fetchDefaultDoas();
    fetchUser();
    fetchGalleryImages();
    fetchGallerySongs();
  }, [cardId]);

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/background-gallery`, getAuthConfig());
      setGalleryImages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch gallery images');
    }
  };

  const fetchGallerySongs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/song-gallery`, getAuthConfig());
      setGallerySongs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch gallery songs');
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/profile`, getAuthConfig());
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user');
    }
  };

  // Thumbnail now uses background_image directly - no need for capture
  // This function is kept for backward compatibility but returns null
  // The actual thumbnail is the background_image itself
  const captureThumbnail = async () => {
    // Just return the background_image URL - no compression, no phone mockup
    // The thumbnail is now handled directly in the save function
    return null;
  };

  // Upload thumbnail and get URL
  const uploadThumbnail = async (blob) => {
    if (!blob) return null;
    
    const formData = new FormData();
    formData.append('file', blob, 'thumbnail.jpg');
    
    try {
      const authConfig = getAuthConfig();
      const response = await axios.post(
        `${BACKEND_URL}/api/upload/thumbnail`,
        formData,
        { 
          ...authConfig,
          headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
        }
      );
      return response.data.url;
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      return null;
    }
  };

  const handleSaveAsTemplate = async () => {
    const templateName = window.prompt('Masukkan nama untuk templat:', `Templat dari ${card.bride_name} & ${card.groom_name}`);
    if (!templateName) return;
    
    setSavingTemplate(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cards/${cardId}/save-as-template?template_name=${encodeURIComponent(templateName)}`,
        {},
        getAuthConfig()
      );
      toast.success('Kad berjaya disimpan sebagai templat!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan sebagai templat');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Set card as preview for landing page (Admin only)
  const handleSetAsPreview = async () => {
    const isCurrentlyPreview = card?.is_preview;
    const confirmMessage = isCurrentlyPreview 
      ? 'Adakah anda pasti mahu buang kad ini dari senarai preview?' 
      : 'Adakah anda pasti mahu jadikan kad ini sebagai preview untuk landing page? Kad akan dikunci dan tidak boleh dipadamkan.';
    
    if (!window.confirm(confirmMessage)) return;
    
    setSavingPreview(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/cards/${cardId}/set-preview`,
        { 
          is_preview: !isCurrentlyPreview,
          preview_thumbnail: card.background_thumbnail || card.background_image
        },
        getAuthConfig()
      );
      
      // Update local card state
      setCard({ ...card, is_preview: !isCurrentlyPreview, is_preview_locked: !isCurrentlyPreview });
      
      toast.success(isCurrentlyPreview ? 'Kad dibuang dari senarai preview!' : 'Kad berjaya dijadikan preview!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemas kini status preview');
    } finally {
      setSavingPreview(false);
    }
  };

  // Export preview as JPEG image (Admin only)
  const handleExportPreview = async () => {
    if (!previewRef.current) {
      toast.error('Preview tidak dijumpai');
      return;
    }
    
    try {
      toast.info('Menjana imej...');
      
      const canvas = await html2canvas(previewRef.current, {
        scale: 2, // Higher quality for export
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false
      });
      
      // Convert to JPEG and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${card.slug || 'preview'}_${Date.now()}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success('Imej berjaya dimuat turun!');
        } else {
          toast.error('Gagal menjana imej');
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Gagal export preview');
    }
  };

  const fetchDefaultDoas = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/doas`);
      setDefaultDoas(response.data);
    } catch (error) {
      console.error('Failed to fetch doas');
    }
  };

  const fetchCard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cards/${cardId}`, getAuthConfig());
      const cardData = {
        ...response.data,
        contacts: response.data.contacts || [],
        gift_wishlist: response.data.gift_wishlist || [],
        aturcara: response.data.aturcara || [],
        social_media: response.data.social_media || [],
        display_parent: response.data.display_parent || 'bride',
        icon_visibility: response.data.icon_visibility || { rsvp: true, gift: true, contact: true, location: true, calendar: true },
        card_colors: response.data.card_colors || DEFAULT_CARD_COLORS
      };
      setCard(cardData);
      
      const templateRes = await axios.get(`${BACKEND_URL}/api/templates`, getAuthConfig());
      const cardTemplate = templateRes.data.find(t => t.template_id === response.data.template_id);
      setTemplate(cardTemplate);
      
      // Fetch RSVP stats and list
      try {
        const statsRes = await axios.get(`${BACKEND_URL}/api/cards/${cardId}/rsvp/stats`, getAuthConfig());
        setRsvpStats(statsRes.data);
        const rsvpRes = await axios.get(`${BACKEND_URL}/api/cards/${cardId}/rsvp`, getAuthConfig());
        setRsvpList(rsvpRes.data);
      } catch (e) {}

      // Fetch Guestbook
      try {
        const guestbookRes = await axios.get(`${BACKEND_URL}/api/cards/${cardId}/guestbook`, getAuthConfig());
        setGuestbookList(guestbookRes.data);
      } catch (e) {}
    } catch (error) {
      toast.error('Gagal memuatkan kad');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const exportRSVP = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cards/${cardId}/rsvp/export`, {
        ...getAuthConfig(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rsvp_${card.slug}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Senarai RSVP berjaya dimuat turun');
    } catch (error) {
      toast.error('Gagal memuat turun senarai RSVP');
    }
  };

  const exportGuestbook = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cards/${cardId}/guestbook/export`, {
        ...getAuthConfig(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `guestbook_${card.slug}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Senarai ucapan berjaya dimuat turun');
    } catch (error) {
      toast.error('Gagal memuat turun senarai ucapan');
    }
  };

  const handleUpdate = async (skipThumbnail = false) => {
    try {
      setCapturingThumbnail(true);
      
      // Thumbnail now uses background_image directly - no capture needed
      // This keeps full quality and uses the actual background image
      const thumbnailUrl = card.background_image;
      
      await axios.put(`${BACKEND_URL}/api/cards/${cardId}`, {
        bride_name: card.bride_name,
        groom_name: card.groom_name,
        bride_parents: card.bride_parents,
        groom_parents: card.groom_parents,
        display_parent: card.display_parent,
        couple_image: card.couple_image,
        gallery_images: card.gallery_images,
        wedding_date: card.wedding_date,
        event_start_time: card.event_start_time,
        event_end_time: card.event_end_time,
        wedding_time: card.wedding_time,
        venue_name: card.venue_name,
        venue_address: card.venue_address,
        venue_map_url: card.venue_map_url,
        venue_lat: card.venue_lat,
        venue_lng: card.venue_lng,
        aturcara: card.aturcara,
        doa: card.doa,
        custom_doa: card.custom_doa,
        hashtag: card.hashtag,
        social_media: card.social_media,
        icon_visibility: card.icon_visibility,
        rsvp_link: card.rsvp_link,
        rsvp_phone: card.rsvp_phone,
        money_gift_bank: card.money_gift_bank,
        money_gift_account: card.money_gift_account,
        money_gift_name: card.money_gift_name,
        gift_qr_image: card.gift_qr_image,
        gift_wishlist: card.gift_wishlist,
        contacts: card.contacts,
        contact_name: card.contact_name,
        contact_phone: card.contact_phone,
        background_image: card.background_image,
        background_thumbnail: thumbnailUrl,
        primary_color: card.primary_color,
        secondary_color: card.secondary_color,
        font_style: card.font_style,
        audio_url: card.audio_url,
        audio_title: card.audio_title,
        card_colors: card.card_colors,
        share_settings: card.share_settings
      }, getAuthConfig());
      toast.success('Kad berjaya dikemas kini!');
      fetchCard();
    } catch (error) {
      toast.error('Gagal mengemas kini kad');
    } finally {
      setCapturingThumbnail(false);
    }
  };

  const handlePublish = async () => {
    try {
      // Capture thumbnail before publishing
      setCapturingThumbnail(true);
      const currentTab = activeTab;
      setActiveTab('onboarding');
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const blob = await captureThumbnail();
      let thumbnailUrl = card.background_thumbnail;
      if (blob) {
        const uploadedUrl = await uploadThumbnail(blob);
        if (uploadedUrl) {
          thumbnailUrl = uploadedUrl;
        }
      }
      
      setActiveTab(currentTab);
      
      await axios.put(`${BACKEND_URL}/api/cards/${cardId}`, { 
        status: 'published',
        background_thumbnail: thumbnailUrl 
      }, getAuthConfig());
      toast.success('Kad berjaya diterbitkan!');
      fetchCard();
    } catch (error) {
      toast.error('Gagal menerbitkan kad');
    } finally {
      setCapturingThumbnail(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Adakah anda pasti mahu memadamkan kad ini?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/cards/${cardId}`, getAuthConfig());
      toast.success('Kad berjaya dipadamkan');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Gagal memadamkan kad');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const authConfig = getAuthConfig();
      const response = await axios.post(`${BACKEND_URL}/api/upload/background`, formData, {
        ...authConfig,
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
      });
      setCard({ ...card, background_image: response.data.url, background_thumbnail: response.data.thumbnail });
      toast.success('Latar belakang berjaya dimuat naik!');
    } catch (error) {
      toast.error('Gagal memuat naik fail');
    } finally {
      setUploading(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingQR(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const authConfig = getAuthConfig();
      const response = await axios.post(`${BACKEND_URL}/api/upload/qr`, formData, {
        ...authConfig,
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
      });
      setCard({ ...card, gift_qr_image: response.data.url });
      toast.success('QR berjaya dimuat naik!');
    } catch (error) {
      toast.error('Gagal memuat naik QR');
    } finally {
      setUploadingQR(false);
    }
  };

  const handleCoupleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCouple(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const authConfig = getAuthConfig();
      const response = await axios.post(`${BACKEND_URL}/api/upload/couple-image`, formData, {
        ...authConfig,
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
      });
      setCard({ ...card, couple_image: response.data.url });
      toast.success('Gambar pasangan berjaya dimuat naik!');
    } catch (error) {
      toast.error('Gagal memuat naik gambar');
    } finally {
      setUploadingCouple(false);
    }
  };

  const handleBackgroundSelect = (bg) => setCard({ ...card, background_image: bg.url, background_thumbnail: bg.thumbnail });
  const handleSongSelect = (song) => setCard({ ...card, audio_url: song.url, audio_title: song.title });

  // Contact management
  const addContact = () => {
    if (card.contacts.length >= 5) { toast.error('Maksimum 5 kenalan sahaja'); return; }
    setCard({ ...card, contacts: [...card.contacts, { name: '', phone: '', role: 'Wakil Keluarga Perempuan' }] });
  };
  const updateContact = (index, field, value) => {
    const newContacts = [...card.contacts];
    newContacts[index][field] = value;
    setCard({ ...card, contacts: newContacts });
  };
  const removeContact = (index) => setCard({ ...card, contacts: card.contacts.filter((_, i) => i !== index) });

  // Wishlist management
  const addWishlistItem = () => setCard({ ...card, gift_wishlist: [...(card.gift_wishlist || []), { item: '', link: '', image: '' }] });
  const updateWishlistItem = (index, field, value) => {
    const newWishlist = [...card.gift_wishlist];
    newWishlist[index][field] = value;
    setCard({ ...card, gift_wishlist: newWishlist });
  };
  const removeWishlistItem = (index) => setCard({ ...card, gift_wishlist: card.gift_wishlist.filter((_, i) => i !== index) });
  
  const uploadWishlistImage = async (index, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/upload/wishlist-image`, formData, getAuthConfig());
      updateWishlistItem(index, 'image', res.data.url);
      toast.success('Gambar dimuat naik!');
    } catch (err) {
      toast.error('Gagal memuat naik gambar');
    }
  };

  // Color management
  const updateCardColor = (colorKey, value) => {
    setCard({
      ...card,
      card_colors: {
        ...(card.card_colors || DEFAULT_CARD_COLORS),
        [colorKey]: value
      }
    });
  };

  // Aturcara management
  const addAturcara = () => setCard({ ...card, aturcara: [...(card.aturcara || []), { time: '', activity: '' }] });
  const updateAturcara = (index, field, value) => {
    const newAturcara = [...card.aturcara];
    newAturcara[index][field] = value;
    setCard({ ...card, aturcara: newAturcara });
  };
  const removeAturcara = (index) => setCard({ ...card, aturcara: card.aturcara.filter((_, i) => i !== index) });

  // Social media management
  const addSocialMedia = () => setCard({ ...card, social_media: [...(card.social_media || []), { platform: 'instagram', url: '' }] });
  const updateSocialMedia = (index, field, value) => {
    const newSocial = [...card.social_media];
    newSocial[index][field] = value;
    setCard({ ...card, social_media: newSocial });
  };
  const removeSocialMedia = (index) => setCard({ ...card, social_media: card.social_media.filter((_, i) => i !== index) });

  // Icon visibility
  const updateIconVisibility = (icon, value) => {
    setCard({ ...card, icon_visibility: { ...card.icon_visibility, [icon]: value } });
  };

  // Share functions
  const getShareUrl = () => `${window.location.origin}/${card.slug}`;
  
  // URL for sharing with OG meta tags (for social media crawlers)
  const getOgShareUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}/api/og/${card.slug}`;
  };
  
  const getShareTitle = () => card.share_settings?.title || `Walimatulurus | ${card.groom_name} & ${card.bride_name}`;
  
  const getShareText = () => card.share_settings?.text || 'Tekan pautan untuk buka kad jemputan | kawkad.com';
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(getOgShareUrl());
    toast.success('Link telah disalin!');
  };
  
  const handleShareWhatsApp = () => {
    // Use OG URL for WhatsApp to get proper preview
    const url = `https://wa.me/?text=${encodeURIComponent(`${getShareTitle()}\n\n${getShareText()}\n\n${getOgShareUrl()}`)}`;
    window.open(url, '_blank');
  };
  
  const handleShareTelegram = () => {
    // Use OG URL for Telegram to get proper preview
    const url = `https://t.me/share/url?url=${encodeURIComponent(getOgShareUrl())}&text=${encodeURIComponent(`${getShareTitle()}\n\n${getShareText()}`)}`;
    window.open(url, '_blank');
  };
  
  const handleShareEmail = () => {
    const subject = encodeURIComponent(getShareTitle());
    const body = encodeURIComponent(`${getShareText()}\n\n${getShareUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Update share settings
  const updateShareSettings = (key, value) => {
    setCard({
      ...card,
      share_settings: {
        ...(card.share_settings || {}),
        [key]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  const fontClass = card.font_style === 'greatvibes' ? 'font-greatvibes' : card.font_style === 'manrope' ? 'font-manrope' : 'font-playfair';

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10">
            ← Kembali
          </Button>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleExportPreview}
                  className="border-[#F7B801] text-[#F7B801] hover:bg-[#F7B801] hover:text-[#3D348B] rounded-full text-sm"
                  title="Export Preview sebagai JPEG"
                >
                  <ImageIcon className="w-4 h-4 mr-1" />
                  Export
                </Button>
                {card.status === 'published' && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShareDialogOpen(true)}
                    className="border-white text-white hover:bg-white hover:text-[#3D348B] rounded-full text-sm"
                    title="Kongsi kad"
                  >
                    <Share2 className="w-4 h-4 mr-1" />
                    Share
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={handleSaveAsTemplate} 
                  disabled={savingTemplate}
                  className="border-purple-600 text-purple-700 hover:bg-purple-50 rounded-full text-sm"
                >
                  {savingTemplate ? 'Menyimpan...' : 'Simpan Sebagai Templat'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSetAsPreview} 
                  disabled={savingPreview || card.status !== 'published'}
                  className={`rounded-full text-sm ${card?.is_preview 
                    ? 'border-red-500 text-red-500 hover:bg-red-50' 
                    : 'border-green-600 text-green-600 hover:bg-green-50'}`}
                  title={card?.is_preview ? 'Buang dari preview' : 'Jadikan sebagai preview untuk landing page'}
                >
                  {savingPreview ? 'Memproses...' : (card?.is_preview ? '🔓 Buang Preview' : '🔒 Jadikan Preview')}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => window.open(`${window.location.origin}/${card.slug}`, '_blank')} disabled={card.status !== 'published'} className="border-[#F7B801] text-[#F18701] hover:bg-gray-50 rounded-full text-sm">
              Preview
            </Button>
            {user?.role !== 'admin' && card.status === 'published' && (
              <Button 
                variant="outline" 
                onClick={() => setShareDialogOpen(true)}
                className="border-white text-white hover:bg-white hover:text-[#3D348B] rounded-full text-sm"
                title="Kongsi kad"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            )}
            <Button onClick={() => handleUpdate(false)} disabled={capturingThumbnail} className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full text-sm font-semibold">
              {capturingThumbnail ? 'Menyimpan...' : 'Simpan'}
            </Button>
            {card.status === 'draft' && (
              <Button onClick={handlePublish} disabled={capturingThumbnail} className="bg-white text-[#3D348B] hover:bg-gray-100 rounded-full text-sm font-semibold">
                {capturingThumbnail ? 'Menerbitkan...' : 'Terbitkan'}
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* LARGER EDITOR (2/3), SMALLER PREVIEW (1/3) */}
        <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
          {/* EDITOR SECTION - LARGER */}
          <div className="bg-white rounded-xl p-5 card-shadow">
            <Tabs defaultValue="onboarding" className="space-y-3" onValueChange={(val) => setActiveTab(val)}>
              <TabsList className="grid w-full grid-cols-8 text-[10px]">
                <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
                <TabsTrigger value="kadinsert">Kad Insert</TabsTrigger>
                <TabsTrigger value="kandungan">Kandungan</TabsTrigger>
                <TabsTrigger value="lokasi">Lokasi</TabsTrigger>
                <TabsTrigger value="hadiah">Hadiah</TabsTrigger>
                <TabsTrigger value="rsvp">RSVP</TabsTrigger>
                <TabsTrigger value="guestbook">Guestbook</TabsTrigger>
                <TabsTrigger value="settings">Tetapan</TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[calc(100vh-220px)]">
                {/* ONBOARDING TAB - Intro/Splash Screen Settings */}
                <TabsContent value="onboarding" className="space-y-4 pr-4">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-[#3D348B]">Kad Pembukaan (Sebelum Klik "Buka Jemputan")</h4>
                    <p className="text-sm text-gray-500">Tetapan untuk paparan awal kad jemputan anda</p>
                    
                    {/* Background Image */}
                    <div className="space-y-2">
                      <Label>Gambar Latar Belakang</Label>
                      <div className="flex items-center gap-3">
                        {card.background_image && (
                          <img src={card.background_image} alt="Background" className="w-24 h-16 object-cover rounded border" />
                        )}
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                          {uploading ? 'Memuat naik...' : <><Upload className="w-4 h-4 mr-2" /> Muat Naik</>}
                        </Button>
                        {galleryImages.length > 0 && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setShowGalleryPicker(true)}
                            className="border-[#7678ED] text-[#7678ED] hover:bg-[#7678ED]/5"
                          >
                            <ImageIcon className="w-4 h-4 mr-2" /> Pilih dari Galeri
                          </Button>
                        )}
                      </div>
                      
                      {/* Gallery Picker Modal */}
                      {showGalleryPicker && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
                            <div className="p-4 border-b flex justify-between items-center">
                              <h3 className="font-playfair font-semibold text-lg">Pilih Gambar Latar Belakang</h3>
                              <Button variant="ghost" size="sm" onClick={() => setShowGalleryPicker(false)}>✕</Button>
                            </div>
                            <div className="p-4 overflow-y-auto max-h-[60vh]">
                              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                {galleryImages.map((img) => (
                                  <div 
                                    key={img.image_id}
                                    onClick={() => {
                                      setCard({ ...card, background_image: img.url, background_thumbnail: img.thumbnail });
                                      setShowGalleryPicker(false);
                                      toast.success('Gambar latar belakang dikemaskini!');
                                    }}
                                    className="cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-[#7678ED]/50 transition-all"
                                  >
                                    <img 
                                      src={img.thumbnail || img.url} 
                                      alt={img.name} 
                                      className="w-full h-24 object-cover"
                                    />
                                    <p className="text-xs p-1 truncate text-center bg-gray-50">{img.name}</p>
                                  </div>
                                ))}
                              </div>
                              {galleryImages.length === 0 && (
                                <p className="text-center text-gray-500 py-8">Tiada gambar dalam galeri</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Overlay Opacity for Intro */}
                    <div className="space-y-2">
                      <Label className="text-xs">Kelegapan Overlay Pembukaan</Label>
                      <Input 
                        type="range" 
                        min="0" max="1" step="0.1"
                        value={card.card_colors?.overlay_opacity || 0.3} 
                        onChange={(e) => updateCardColor('overlay_opacity', parseFloat(e.target.value))} 
                      />
                      <span className="text-xs text-gray-500">{Math.round((card.card_colors?.overlay_opacity || 0.3) * 100)}%</span>
                    </div>

                    {/* Onboarding Colors */}
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-[#3D348B] mb-3">Warna Onboarding</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Warna Aksen (Nama)</Label>
                          <Input type="color" value={card.secondary_color} onChange={(e) => setCard({ ...card, secondary_color: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Warna Text</Label>
                          <Input type="color" value={card.card_colors?.text_primary || '#FFFFFF'} onChange={(e) => updateCardColor('text_primary', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Button Buka (BG)</Label>
                          <Input type="color" value={card.card_colors?.onboarding_button_bg || '#FFFFFF'} onChange={(e) => updateCardColor('onboarding_button_bg', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Button Buka (Text)</Label>
                          <Input type="color" value={card.card_colors?.onboarding_button_text || '#000000'} onChange={(e) => updateCardColor('onboarding_button_text', e.target.value)} />
                        </div>
                      </div>
                    </div>

                    {/* Music Selection */}
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-[#3D348B] mb-3">Muzik Latar</h4>
                      
                      {/* Source Selection */}
                      <div className="flex gap-2 mb-3">
                        <Button
                          type="button"
                          variant={audioSource === 'gallery' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setAudioSource('gallery')}
                          className={audioSource === 'gallery' ? 'bg-[#3D348B]' : ''}
                        >
                          Pilih dari Galeri
                        </Button>
                        <Button
                          type="button"
                          variant={audioSource === 'custom' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setAudioSource('custom')}
                          className={audioSource === 'custom' ? 'bg-[#3D348B]' : ''}
                        >
                          URL Custom
                        </Button>
                      </div>
                      
                      {/* Gallery Songs */}
                      {audioSource === 'gallery' && (
                        <div className="space-y-2">
                          {gallerySongs.length > 0 ? (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {gallerySongs.map((song) => (
                                <div 
                                  key={song.song_id} 
                                  onClick={() => setCard({ ...card, audio_url: song.url, audio_title: `${song.name}${song.artist ? ` - ${song.artist}` : ''}` })} 
                                  className={`p-2 rounded-lg border cursor-pointer transition-all text-xs ${card.audio_url === song.url ? 'border-[#3D348B] bg-[#7678ED]/5' : 'border-gray-200 hover:bg-gray-50'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {card.audio_url === song.url && <Check className="w-3 h-3 text-[#3D348B] flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{song.name}</p>
                                      {song.artist && <p className="text-gray-500 truncate">{song.artist}</p>}
                                    </div>
                                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded capitalize">{song.category}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 py-4 text-center">Tiada lagu dalam galeri. Admin perlu muat naik lagu terlebih dahulu.</p>
                          )}
                        </div>
                      )}

                      {/* Custom URL Input */}
                      {audioSource === 'custom' && (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500">Masukkan URL fail audio (MP3, WAV, dll). YouTube tidak disokong.</p>
                          <Input 
                            value={card.audio_url || ''} 
                            onChange={(e) => setCard({ ...card, audio_url: e.target.value })} 
                            placeholder="https://example.com/lagu.mp3"
                            className="text-xs"
                          />
                          <Label className="text-xs">Tajuk Lagu:</Label>
                          <Input 
                            value={card.audio_title || ''} 
                            onChange={(e) => setCard({ ...card, audio_title: e.target.value })} 
                            placeholder="Contoh: Perfect - Ed Sheeran"
                            className="text-xs"
                          />
                        </div>
                      )}

                      {/* Current Selection & Clear */}
                      {card.audio_url && (
                        <div className="mt-3 p-2 bg-[#7678ED]/5 rounded-lg">
                          <p className="text-xs text-[#7678ED] flex items-center gap-1">
                            <Check className="w-3 h-3" /> 
                            <span className="font-medium">Lagu dipilih:</span> {card.audio_title || 'Tanpa tajuk'}
                          </p>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setCard({ ...card, audio_url: '', audio_title: '' })}
                            className="text-xs text-red-500 hover:text-red-600 mt-1 h-6"
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Buang Muzik
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* KAD INSERT TAB - Main Card Settings */}
                <TabsContent value="kadinsert" className="space-y-4 pr-4">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-[#3D348B]">Reka Bentuk Kad (Selepas Klik "Buka Jemputan")</h4>
                    <p className="text-sm text-gray-500">Warna dan gaya untuk paparan utama kad</p>
                    
                    {/* Custom Colors */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Warna Icon</Label>
                        <Input type="color" value={card.primary_color} onChange={(e) => setCard({ ...card, primary_color: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Warna Countdown</Label>
                        <Input type="color" value={card.card_colors?.countdown_text || '#FFFFFF'} onChange={(e) => updateCardColor('countdown_text', e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Warna Butang Ucapan</Label>
                        <Input type="color" value={card.card_colors?.button_bg || '#064E3B'} onChange={(e) => updateCardColor('button_bg', e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Warna Butang Text Ucapan</Label>
                        <Input type="color" value={card.card_colors?.button_text || '#FFFFFF'} onChange={(e) => updateCardColor('button_text', e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Pilih Font Nama Pengantin</Label>
                        <Select value={card.font_style} onValueChange={(value) => setCard({ ...card, font_style: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="playfair">Playfair Display</SelectItem>
                            <SelectItem value="greatvibes">Great Vibes</SelectItem>
                            <SelectItem value="dancing">Dancing Script</SelectItem>
                            <SelectItem value="pinyon">Pinyon Script</SelectItem>
                            <SelectItem value="tangerine">Tangerine</SelectItem>
                            <SelectItem value="alex">Alex Brush</SelectItem>
                            <SelectItem value="allura">Allura</SelectItem>
                            <SelectItem value="sacramento">Sacramento</SelectItem>
                            <SelectItem value="cormorant">Cormorant Garamond</SelectItem>
                            <SelectItem value="cinzel">Cinzel</SelectItem>
                            <SelectItem value="manrope">Manrope</SelectItem>
                            <SelectItem value="lora">Lora</SelectItem>
                            <SelectItem value="italiana">Italiana</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* KANDUNGAN TAB */}
                <TabsContent value="kandungan" className="space-y-4 pr-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nama Pengantin Perempuan</Label>
                      <Input value={card.bride_name} onChange={(e) => setCard({ ...card, bride_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Nama Pengantin Lelaki</Label>
                      <Input value={card.groom_name} onChange={(e) => setCard({ ...card, groom_name: e.target.value })} />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ibu Bapa Pengantin Perempuan</Label>
                      <Input value={card.bride_parents || ''} onChange={(e) => setCard({ ...card, bride_parents: e.target.value })} placeholder="Encik ... & Puan ..." />
                    </div>
                    <div className="space-y-2">
                      <Label>Ibu Bapa Pengantin Lelaki</Label>
                      <Input value={card.groom_parents || ''} onChange={(e) => setCard({ ...card, groom_parents: e.target.value })} placeholder="Encik ... & Puan ..." />
                    </div>
                  </div>

                  {/* Display Parent Selection */}
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <Label>Paparan Nama Ibu Bapa di Atas Kad</Label>
                    <p className="text-xs text-gray-500">Pilih nama ibu bapa siapa yang akan dipaparkan selepas salam</p>
                    <Select value={card.display_parent || 'bride'} onValueChange={(v) => setCard({ ...card, display_parent: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bride">Ibu Bapa Pengantin Perempuan Sahaja</SelectItem>
                        <SelectItem value="groom">Ibu Bapa Pengantin Lelaki Sahaja</SelectItem>
                        <SelectItem value="both">Dua-dua Belah Pihak</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Gallery Images (Multi-upload carousel) */}
                  <div className="border-t pt-4"></div>
                  <div className="space-y-2">
                    <Label>Galeri Gambar (Carousel - Max 5)</Label>
                    <p className="text-xs text-gray-500 mb-2">Gambar-gambar ini akan dipaparkan dalam carousel di kad anda</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(card.gallery_images || []).map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt={`Gallery ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border" />
                          <button
                            type="button"
                            onClick={() => setCard({ ...card, gallery_images: card.gallery_images.filter((_, i) => i !== idx) })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {(!card.gallery_images || card.gallery_images.length < 5) && (
                        <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#3D348B] transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []);
                              const currentCount = card.gallery_images?.length || 0;
                              const remainingSlots = 5 - currentCount;
                              const filesToUpload = files.slice(0, remainingSlots);
                              
                              if (files.length > remainingSlots) {
                                toast.error(`Hanya ${remainingSlots} gambar lagi boleh dimuat naik`);
                              }
                              
                              for (const file of filesToUpload) {
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formData, {
                                    ...getAuthConfig(),
                                    headers: { ...getAuthConfig().headers, 'Content-Type': 'multipart/form-data' }
                                  });
                                  setCard(prev => ({
                                    ...prev,
                                    gallery_images: [...(prev.gallery_images || []), response.data.url]
                                  }));
                                } catch (error) {
                                  toast.error('Gagal memuat naik gambar');
                                }
                              }
                              e.target.value = '';
                            }}
                          />
                          <Plus className="w-6 h-6 text-gray-400" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{card.gallery_images?.length || 0}/5 gambar</p>
                  </div>

                  {/* Date & Time */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-[#3D348B] mb-3">Tarikh & Masa</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Tarikh Majlis</Label>
                        <Input type="date" value={card.wedding_date} onChange={(e) => setCard({ ...card, wedding_date: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Majlis Mula</Label>
                        <Input type="time" value={card.event_start_time || card.wedding_time || ''} onChange={(e) => setCard({ ...card, event_start_time: e.target.value, wedding_time: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Majlis Tamat</Label>
                        <Input type="time" value={card.event_end_time || ''} onChange={(e) => setCard({ ...card, event_end_time: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {/* Venue */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-[#3D348B] mb-3">Tempat Majlis</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nama Venue</Label>
                        <Input value={card.venue_name} onChange={(e) => setCard({ ...card, venue_name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Alamat Venue</Label>
                        <Textarea value={card.venue_address} onChange={(e) => setCard({ ...card, venue_address: e.target.value })} rows={2} />
                      </div>
                    </div>
                  </div>

                  {/* Aturcara Majlis */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-[#3D348B]">Aturcara Majlis</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addAturcara}>
                        <Plus className="w-4 h-4 mr-1" /> Tambah
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {card.aturcara && card.aturcara.map((item, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <Input value={item.time} onChange={(e) => updateAturcara(idx, 'time', e.target.value)} placeholder="11:00 AM" className="w-28" />
                          <Input value={item.activity} onChange={(e) => updateAturcara(idx, 'activity', e.target.value)} placeholder="Majlis Bermula" className="flex-1" />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeAturcara(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Doa */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-[#3D348B] mb-3">Doa</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pilih Doa</Label>
                        <Select value={card.custom_doa ? 'custom' : 'preset'} onValueChange={(v) => {
                          if (v === 'custom') {
                            setCard({ ...card, custom_doa: true, doa: '' });
                          } else {
                            setCard({ ...card, custom_doa: false });
                          }
                        }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="preset">Pilih Doa Sedia Ada</SelectItem>
                            <SelectItem value="custom">Tulis Sendiri</SelectItem>
                          </SelectContent>
                        </Select>
                        {!card.custom_doa && (
                          <Select value={card.doa || ''} onValueChange={(v) => setCard({ ...card, doa: v })}>
                            <SelectTrigger><SelectValue placeholder="Pilih doa..." /></SelectTrigger>
                            <SelectContent>
                              {defaultDoas.map(d => (
                                <SelectItem key={d.id} value={d.text}>{d.title}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Doa {card.custom_doa ? '(Tulis Sendiri)' : ''}</Label>
                        <Textarea value={card.doa || ''} onChange={(e) => setCard({ ...card, doa: e.target.value })} placeholder="Masukkan doa..." rows={3} disabled={!card.custom_doa} />
                      </div>
                    </div>
                  </div>

                  {/* Hashtag & Social Media */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-[#3D348B] mb-3">Hashtag & Media Sosial</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Hashtag Perkahwinan</Label>
                        <Input value={card.hashtag || ''} onChange={(e) => setCard({ ...card, hashtag: e.target.value })} placeholder="AbuloveNisa" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label>Link Media Sosial</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addSocialMedia}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        {card.social_media && card.social_media.map((social, idx) => (
                          <div key={idx} className="flex gap-1 items-center">
                            <Select value={social.platform} onValueChange={(v) => updateSocialMedia(idx, 'platform', v)}>
                              <SelectTrigger className="w-24 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="instagram">Instagram</SelectItem>
                                <SelectItem value="facebook">Facebook</SelectItem>
                                <SelectItem value="tiktok">TikTok</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input value={social.url} onChange={(e) => updateSocialMedia(idx, 'url', e.target.value)} placeholder="URL" className="flex-1 text-xs" />
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeSocialMedia(idx)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Senarai Boleh Hubungi - Now in Kandungan */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-[#3D348B]">Senarai Boleh Hubungi ({card.contacts?.length || 0}/5)</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addContact} disabled={card.contacts?.length >= 5}>
                        <Plus className="w-4 h-4 mr-1" /> Tambah
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {card.contacts && card.contacts.map((contact, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium">Ahli keluarga {idx + 1}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(idx)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                          <Input value={contact.name} onChange={(e) => updateContact(idx, 'name', e.target.value)} placeholder="Nama" className="text-sm" />
                          <Input value={contact.phone} onChange={(e) => updateContact(idx, 'phone', e.target.value)} placeholder="012-3456789" className="text-sm" />
                          <Select value={contact.role} onValueChange={(v) => updateContact(idx, 'role', v)}>
                            <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Wakil Keluarga Perempuan">Wakil Keluarga Perempuan</SelectItem>
                              <SelectItem value="Wakil Keluarga Lelaki">Wakil Keluarga Lelaki</SelectItem>
                              <SelectItem value="Pengantin Perempuan">Pengantin Perempuan</SelectItem>
                              <SelectItem value="Pengantin Lelaki">Pengantin Lelaki</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* FEATURES TAB */}
                <TabsContent value="lokasi" className="space-y-4 pr-4">
                  {/* Location */}
                  <div>
                    <h4 className="font-semibold text-[#3D348B] mb-3">Lokasi</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Link Google Maps</Label>
                        <Input value={card.venue_map_url || ''} onChange={(e) => setCard({ ...card, venue_map_url: e.target.value })} placeholder="https://goo.gl/maps/..." />
                      </div>
                      <div className="space-y-2">
                        <Label>Latitude</Label>
                        <Input value={card.venue_lat || ''} onChange={(e) => setCard({ ...card, venue_lat: e.target.value })} placeholder="3.1390" />
                      </div>
                      <div className="space-y-2">
                        <Label>Longitude</Label>
                        <Input value={card.venue_lng || ''} onChange={(e) => setCard({ ...card, venue_lng: e.target.value })} placeholder="101.6869" />
                      </div>
                    </div>
                  </div>

                </TabsContent>

                {/* HADIAH TAB (was Wishlist) */}
                <TabsContent value="hadiah" className="space-y-4 pr-4">
                  {/* QR & Akaun No Section (was Hadiah) */}
                  <div>
                    <h4 className="font-semibold text-[#F7B801] mb-3">QR & Akaun No</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>QR Code Pembayaran</Label>
                        {card.gift_qr_image && <img src={card.gift_qr_image} alt="QR" className="w-24 h-24 object-contain border rounded-lg" />}
                        <input ref={qrInputRef} type="file" accept="image/*" onChange={handleQRUpload} className="hidden" />
                        <Button type="button" variant="outline" onClick={() => qrInputRef.current?.click()} disabled={uploadingQR} className="w-full text-xs">
                          <Upload className="w-3 h-3 mr-1" />
                          {uploadingQR ? 'Memuat naik...' : 'Muat Naik QR'}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label>Maklumat Bank</Label>
                        <Input value={card.money_gift_bank || ''} onChange={(e) => setCard({ ...card, money_gift_bank: e.target.value })} placeholder="Maybank" className="text-sm" />
                        <Input value={card.money_gift_account || ''} onChange={(e) => setCard({ ...card, money_gift_account: e.target.value })} placeholder="No. Akaun" className="text-sm" />
                        <Input value={card.money_gift_name || ''} onChange={(e) => setCard({ ...card, money_gift_name: e.target.value })} placeholder="Nama Penerima" className="text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Wishlist Section */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-[#F7B801]">Senarai Hajat (Wishlist)</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addWishlistItem}>
                        <Plus className="w-4 h-4 mr-1" /> Tambah
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Tambah barang yang anda inginkan sebagai hadiah. Tetamu boleh "reserve" item supaya tidak terbeli berkali-kali.</p>
                    <div className="space-y-3 mt-3">
                      {card.gift_wishlist && card.gift_wishlist.map((item, idx) => (
                        <div key={idx} className={`p-3 rounded-lg space-y-2 ${item.reserved_by ? 'bg-gray-200' : 'bg-gray-50'}`}>
                          <div className="flex gap-2 items-start">
                            {/* Image upload */}
                            <div className="flex-shrink-0">
                              {item.image ? (
                                <img src={item.image} alt={item.item} className="w-16 h-16 object-cover rounded border" />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded border flex items-center justify-center">
                                  <ImageIcon className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <input 
                                type="file" 
                                accept="image/*" 
                                className="hidden" 
                                id={`wishlist-img-${idx}`}
                                onChange={(e) => e.target.files[0] && uploadWishlistImage(idx, e.target.files[0])}
                                disabled={!!item.reserved_by}
                              />
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                className="w-16 text-[10px] mt-1 p-1 h-6"
                                onClick={() => document.getElementById(`wishlist-img-${idx}`)?.click()}
                                disabled={!!item.reserved_by}
                              >
                                Upload
                              </Button>
                            </div>
                            <div className="flex-1 space-y-2">
                              <Input value={item.item} onChange={(e) => updateWishlistItem(idx, 'item', e.target.value)} placeholder="Nama barang" disabled={!!item.reserved_by} />
                              <Input value={item.link || ''} onChange={(e) => updateWishlistItem(idx, 'link', e.target.value)} placeholder="Link (Shopee/Lazada)" disabled={!!item.reserved_by} />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeWishlistItem(idx)} disabled={!!item.reserved_by}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        {item.reserved_by && (
                          <div className="flex items-center justify-between bg-gray-100 p-2 rounded text-sm">
                            <span className="text-amber-800">✓ Ditempah oleh: <strong>{item.reserved_by}</strong></span>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              className="text-xs h-7"
                              onClick={async () => {
                                try {
                                  await axios.post(`${BACKEND_URL}/api/cards/${cardId}/wishlist/${idx}/clear`, {}, getAuthConfig());
                                  toast.success('Tempahan berjaya dibatalkan');
                                  fetchCard();
                                } catch (err) {
                                  toast.error('Gagal membatalkan tempahan');
                                }
                              }}
                            >
                              Batal Tempahan
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {(!card.gift_wishlist || card.gift_wishlist.length === 0) && (
                      <p className="text-center text-gray-400 py-4">Tiada item wishlist</p>
                    )}
                    </div>
                  </div>
                </TabsContent>

                {/* RSVP TAB */}
                <TabsContent value="rsvp" className="space-y-4 pr-4">
                  <h4 className="font-semibold text-[#3D348B]">Statistik RSVP</h4>
                  {rsvpStats && (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-[#7678ED]/5 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#3D348B]">{rsvpStats.attending}</p>
                        <p className="text-xs text-gray-600">Hadir</p>
                      </div>
                      <div className="bg-red-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-600">{rsvpStats.not_attending}</p>
                        <p className="text-xs text-gray-600">Tidak Hadir</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#F7B801]">{rsvpStats.total_guests}</p>
                        <p className="text-xs text-gray-600">Jumlah Tetamu</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{rsvpStats.total_responses}</p>
                        <p className="text-xs text-gray-600">Respons</p>
                      </div>
                    </div>
                  )}
                  
                  <Button onClick={exportRSVP} className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED]">
                    <Download className="w-4 h-4 mr-2" /> Muat Turun Senarai RSVP (Excel)
                  </Button>

                  {/* RSVP List */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Senarai RSVP ({rsvpList.length})</h4>
                    {rsvpList.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Tiada RSVP lagi</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {rsvpList.map((rsvp, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${rsvp.attending ? 'bg-[#7678ED]/5' : 'bg-red-50'}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-sm">{rsvp.name}</p>
                                <p className="text-xs text-gray-600">{rsvp.phone}</p>
                              </div>
                              <div className="text-right">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${rsvp.attending ? 'bg-emerald-200 text-[#7678ED]' : 'bg-red-200 text-red-800'}`}>
                                  {rsvp.attending ? 'Hadir' : 'Tidak'}
                                </span>
                                <p className="text-xs text-gray-500 mt-1">{rsvp.num_guests} tetamu</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* GUESTBOOK TAB */}
                <TabsContent value="guestbook" className="space-y-4 pr-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-[#3D348B] flex items-center gap-2">
                      <BookOpen className="w-5 h-5" /> Koleksi Ucapan
                    </h4>
                    <Button onClick={exportGuestbook} variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" /> Export Excel
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">Ucapan dan doa daripada tetamu</p>
                  
                  {guestbookList.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p>Tiada ucapan lagi</p>
                      <p className="text-xs mt-1">Ucapan akan muncul di sini apabila tetamu menghantar ucapan</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {guestbookList.map((entry, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm italic mb-2">"{entry.message}"</p>
                          <p className="text-xs text-gray-600">— {entry.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* SETTINGS TAB */}
                <TabsContent value="settings" className="space-y-4 pr-4">
                  <h4 className="font-semibold text-[#3D348B]">Tetapan Paparan Ikon</h4>
                  <p className="text-sm text-gray-500">Pilih ikon yang mahu dipaparkan di bahagian bawah kad</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'rsvp', label: 'RSVP', icon: '💬' },
                      { key: 'gift', label: 'Hadiah', icon: '🎁' },
                      { key: 'contact', label: 'Hubungi', icon: '📞' },
                      { key: 'location', label: 'Lokasi', icon: '📍' },
                      { key: 'calendar', label: 'Kalendar', icon: '📅' }
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span>{item.icon}</span>
                          <span className="text-sm">{item.label}</span>
                        </div>
                        <Switch 
                          checked={card.icon_visibility?.[item.key] !== false} 
                          onCheckedChange={(checked) => updateIconVisibility(item.key, checked)}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Share & SEO Settings */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-[#3D348B] flex items-center gap-2">
                      <Share2 className="w-4 h-4" /> Tetapan Perkongsian & SEO
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">Tetapan untuk perkongsian di media sosial</p>
                    
                    {/* Preview Thumbnail */}
                    <div className="mb-4">
                      <Label className="text-xs text-gray-600 mb-2 block">Preview Thumbnail</Label>
                      <div 
                        className="w-full h-40 rounded-lg overflow-hidden relative"
                        style={{ 
                          backgroundImage: `url(${card.background_image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <p className="text-white text-lg font-semibold text-center px-4" style={{ fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>
                            Anda Dijemput Hadir
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Thumbnail ini akan dipaparkan apabila pautan dikongsi</p>
                    </div>
                    
                    {/* SEO Title */}
                    <div className="space-y-2 mb-4">
                      <Label className="text-xs">SEO Title / Tajuk Perkongsian</Label>
                      <Input
                        value={card.share_settings?.title || ''}
                        onChange={(e) => updateShareSettings('title', e.target.value)}
                        placeholder={`Walimatulurus | ${card.groom_name} & ${card.bride_name}`}
                      />
                      <p className="text-xs text-gray-400">Default: Walimatulurus | Nama pengantin</p>
                    </div>
                    
                    {/* Share Text */}
                    <div className="space-y-2 mb-4">
                      <Label className="text-xs">Ayat Perkongsian</Label>
                      <Textarea
                        value={card.share_settings?.text || ''}
                        onChange={(e) => updateShareSettings('text', e.target.value)}
                        placeholder="Tekan pautan untuk buka kad jemputan | kawkad.com"
                        rows={2}
                      />
                      <p className="text-xs text-gray-400">Default: Tekan pautan untuk buka kad jemputan | kawkad.com</p>
                    </div>

                    {/* Social Media Preview Cards */}
                    <div className="space-y-3">
                      <Label className="text-xs text-gray-600">Preview Perkongsian</Label>
                      
                      {/* WhatsApp Preview */}
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="w-4 h-4 text-[#3D348B]" />
                          <span className="text-sm font-medium text-green-700">WhatsApp</span>
                        </div>
                        <div className="bg-white rounded p-2 text-xs">
                          <p className="font-semibold">{card.share_settings?.title || `Walimatulurus | ${card.groom_name} & ${card.bride_name}`}</p>
                          <p className="text-gray-600 mt-1">{card.share_settings?.text || 'Tekan pautan untuk buka kad jemputan | kawkad.com'}</p>
                          <p className="text-blue-600 mt-1">{window.location.origin}/{card.slug}</p>
                        </div>
                      </div>
                      
                      {/* Telegram Preview */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Send className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Telegram</span>
                        </div>
                        <div className="bg-white rounded p-2 text-xs">
                          <p className="font-semibold">{card.share_settings?.title || `Walimatulurus | ${card.groom_name} & ${card.bride_name}`}</p>
                          <p className="text-gray-600 mt-1">{card.share_settings?.text || 'Tekan pautan untuk buka kad jemputan | kawkad.com'}</p>
                        </div>
                      </div>
                      
                      {/* Email Preview */}
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Mail className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">Email</span>
                        </div>
                        <div className="bg-white rounded p-2 text-xs">
                          <p className="text-gray-500">Subject:</p>
                          <p className="font-semibold">{card.share_settings?.title || `Walimatulurus | ${card.groom_name} & ${card.bride_name}`}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-6">
                    {card?.is_preview_locked && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        🔒 Kad ini dikunci sebagai preview dan tidak boleh dipadamkan.
                      </div>
                    )}
                    <Button 
                      variant="destructive" 
                      onClick={handleDelete} 
                      className="w-full rounded-full"
                      disabled={card?.is_preview_locked}
                    >
                      Padam Kad
                    </Button>
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>

          {/* PREVIEW SECTION - Narrow card style like reference */}
          <div className="bg-gray-100 rounded-xl p-4 card-shadow h-fit sticky top-20">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">
              Preview {activeTab === 'onboarding' ? '(Onboarding)' : '(Kad Insert)'}
            </h3>
            <div className="flex justify-center">
              <div 
                ref={previewRef}
                className="w-[180px] h-[360px] bg-white rounded-lg overflow-hidden relative" 
                style={{ 
                  backgroundImage: `url(${card.background_image})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}
              >
                {/* Onboarding Preview - Intro Screen */}
                {activeTab === 'onboarding' ? (
                  <div 
                    className="h-full flex flex-col items-center justify-center text-center p-3"
                    style={{ 
                      backgroundColor: `rgba(0,0,0,${card.card_colors?.overlay_opacity || 0.1})`,
                      color: card.card_colors?.text_primary || '#4a4a4a'
                    }}
                  >
                    <p className="text-[7px] tracking-[0.15em] mb-3 opacity-80 uppercase">Walimatulurus</p>
                    <h1 className="text-sm mb-1" style={{ color: card.secondary_color || '#4a4a4a', fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>{card.bride_name}</h1>
                    <p className="text-xs my-1" style={{ fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>&</p>
                    <h1 className="text-sm mt-1" style={{ color: card.secondary_color || '#4a4a4a', fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>{card.groom_name}</h1>
                    <div 
                      className="mt-4 px-3 py-1.5 rounded-full text-[7px] font-medium uppercase"
                      style={{
                        backgroundColor: card.card_colors?.onboarding_button_bg || '#FFFFFF',
                        color: card.card_colors?.onboarding_button_text || '#000000'
                      }}
                    >
                      Buka
                    </div>
                  </div>
                ) : (
                  /* Kad Insert Preview - Main Card */
                  <div className="h-full flex flex-col">
                    <div 
                      className="flex-1 overflow-y-auto scrollbar-hide p-3 pt-3 pb-2"
                      style={{ backgroundColor: `rgba(0,0,0,${card.card_colors?.overlay_opacity || 0.1})` }}
                    >
                      <div className="space-y-1.5 text-center" style={{ color: card.card_colors?.text_primary || '#4a4a4a' }}>
                        <p className="text-[7px] tracking-[0.1em] opacity-80 uppercase">Walimatulurus</p>
                        
                        {/* Parents Names */}
                        {(card.display_parent === 'bride' || card.display_parent === 'both') && card.bride_parents && (
                          <p className="text-[5px] opacity-70">{card.bride_parents}</p>
                        )}
                        {(card.display_parent === 'groom' || card.display_parent === 'both') && card.groom_parents && (
                          <p className="text-[5px] opacity-70">{card.groom_parents}</p>
                        )}
                        
                        {/* Couple Names */}
                        <div className="space-y-0.5 py-1">
                          {card.bride_name && <h1 className="text-xs" style={{ color: card.secondary_color || '#4a4a4a', fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>{card.bride_name}</h1>}
                          <p className="text-[8px]" style={{ fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>&</p>
                          {card.groom_name && <h1 className="text-xs" style={{ color: card.secondary_color || '#4a4a4a', fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>{card.groom_name}</h1>}
                        </div>
                        
                        {/* Date */}
                        {card.wedding_date && (
                          <p className="text-[6px] font-medium uppercase tracking-wide">
                            {new Date(card.wedding_date).toLocaleDateString('ms-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        )}
                        
                        {/* Venue Name & Address */}
                        {card.venue_name && <p className="text-[6px] font-medium uppercase">{card.venue_name}</p>}
                        {card.venue_address && <p className="text-[5px] opacity-70 px-2">{card.venue_address}</p>}
                        
                        {/* Countdown Timer Preview */}
                        {card.wedding_date && (
                          <div className="pt-2">
                            <p className="text-[5px] tracking-widest mb-1 opacity-80">MENANTI HARI BAHAGIA</p>
                            <div className="flex justify-center gap-1">
                              {['HARI', 'JAM', 'MINIT', 'SAAT'].map((label, idx) => (
                                <div 
                                  key={idx} 
                                  className="rounded p-1 min-w-[28px]"
                                  style={{ backgroundColor: card.card_colors?.countdown_bg || 'rgba(255,255,255,0.1)' }}
                                >
                                  <div className="text-[8px] font-bold" style={{ color: card.card_colors?.countdown_text || '#FFFFFF' }}>
                                    00
                                  </div>
                                  <div className="text-[4px] opacity-70">{label}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Aturcara Majlis Preview */}
                        {card.aturcara && card.aturcara.length > 0 && (
                          <div className="pt-2">
                            <p className="text-[5px] tracking-widest mb-1 opacity-80">ATURCARA</p>
                            <div className="space-y-0.5">
                              {card.aturcara.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="text-[5px] flex justify-center gap-2">
                                  <span className="opacity-70">{item.time}</span>
                                  <span>{item.activity}</span>
                                </div>
                              ))}
                              {card.aturcara.length > 3 && (
                                <p className="text-[4px] opacity-50">+{card.aturcara.length - 3} lagi</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Gallery Images Preview (Actual thumbnails) */}
                        {card.gallery_images && card.gallery_images.length > 0 && (
                          <div className="pt-2">
                            <p className="text-[5px] tracking-widest mb-1 opacity-80">GALERI</p>
                            <div className="flex justify-center gap-0.5">
                              {card.gallery_images.slice(0, 5).map((img, idx) => (
                                <div 
                                  key={idx} 
                                  className="w-4 h-4 rounded-sm overflow-hidden border border-white/30"
                                >
                                  <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Doa Preview */}
                        {(card.doa || card.custom_doa) && (
                          <div className="pt-2">
                            <p className="text-[5px] tracking-widest mb-1 opacity-80">DOA</p>
                            <p className="text-[5px] italic opacity-70 px-2 line-clamp-2">
                              {card.custom_doa || (defaultDoas.find(d => d.doa_id === card.doa)?.text?.substring(0, 50) + '...')}
                            </p>
                          </div>
                        )}
                        
                        {/* Hashtag Preview */}
                        {card.hashtag && (
                          <div className="pt-1">
                            <p className="text-[6px] font-medium" style={{ color: card.secondary_color }}>#{card.hashtag}</p>
                          </div>
                        )}
                        
                        {/* Guestbook Button Preview */}
                        <div className="pt-2">
                          <div 
                            className="inline-block rounded px-2 py-0.5 text-[5px]"
                            style={{ 
                              backgroundColor: card.card_colors?.button_bg || '#064E3B',
                              color: card.card_colors?.button_text || '#FFFFFF'
                            }}
                          >
                            Ucapan ({guestbookList.length})
                          </div>
                        </div>
                        
                        {/* Guestbook Preview */}
                        {guestbookList.length > 0 && (
                          <div className="pt-1">
                            <div 
                              className="rounded p-1.5 text-left"
                              style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                            >
                              <p className="text-[5px] italic line-clamp-2">"{guestbookList[0]?.message}"</p>
                              <p className="text-[4px] mt-0.5" style={{ color: card.secondary_color }}>— {guestbookList[0]?.name}</p>
                            </div>
                            {guestbookList.length > 1 && (
                              <p className="text-[4px] opacity-50 mt-0.5">+{guestbookList.length - 1} lagi ucapan</p>
                            )}
                          </div>
                        )}
                        
                        {/* Social Media Preview */}
                        {card.social_media && card.social_media.length > 0 && (
                          <div className="pt-2 flex justify-center gap-2">
                            {card.social_media.slice(0, 4).map((social, idx) => (
                              <div key={idx} className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                                <span className="text-[5px]">
                                  {social.platform === 'instagram' ? 'IG' : 
                                   social.platform === 'facebook' ? 'FB' : 
                                   social.platform === 'tiktok' ? 'TT' : 
                                   social.platform === 'twitter' ? 'X' : '?'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Contacts Preview */}
                        {card.contacts && card.contacts.length > 0 && (
                          <div className="pt-2">
                            <p className="text-[5px] tracking-widest mb-1 opacity-80">HUBUNGI</p>
                            <div className="space-y-0.5">
                              {card.contacts.slice(0, 2).map((contact, idx) => (
                                <p key={idx} className="text-[5px]">{contact.name}: {contact.phone}</p>
                              ))}
                              {card.contacts.length > 2 && (
                                <p className="text-[4px] opacity-50">+{card.contacts.length - 2} lagi</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Bottom Nav Preview */}
                    <div className="bg-white border-t border-gray-100 px-1 py-1.5">
                      <div className="flex justify-around text-[5px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                        {card.icon_visibility?.rsvp !== false && <span className="text-center">RSVP</span>}
                        {card.icon_visibility?.gift !== false && <span className="text-center">Hadiah</span>}
                        {card.icon_visibility?.contact !== false && <span className="text-center">Hubungi</span>}
                        {card.icon_visibility?.location !== false && <span className="text-center">Lokasi</span>}
                        {card.icon_visibility?.calendar !== false && <span className="text-center">Kalendar</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-center text-gray-400 text-[10px] mt-2">/{card.slug}</p>
          </div>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5" /> Kongsi Kad Jemputan
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Thumbnail Preview */}
            <div 
              className="w-full h-32 rounded-lg overflow-hidden relative"
              style={{ 
                backgroundImage: `url(${card.background_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <p className="text-white text-lg font-semibold text-center px-4" style={{ fontFamily: FONT_FAMILIES[card.font_style] || FONT_FAMILIES['playfair'] }}>
                  Anda Dijemput Hadir
                </p>
              </div>
            </div>
            
            {/* Share Link */}
            <div className="flex gap-2">
              <Input 
                value={getOgShareUrl()} 
                readOnly 
                className="text-sm bg-gray-50"
              />
              <Button onClick={handleCopyLink} variant="outline" size="icon">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={handleCopyLink}
                variant="outline"
                className="flex items-center gap-2 justify-center py-6"
              >
                <Link className="w-5 h-5" />
                <span>Copy Link</span>
              </Button>
              
              <Button 
                onClick={handleShareWhatsApp}
                className="flex items-center gap-2 justify-center py-6 bg-[#3D348B] hover:bg-[#7678ED] text-white"
              >
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp</span>
              </Button>
              
              <Button 
                onClick={handleShareTelegram}
                className="flex items-center gap-2 justify-center py-6 bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Send className="w-5 h-5" />
                <span>Telegram</span>
              </Button>
              
              <Button 
                onClick={handleShareEmail}
                variant="outline"
                className="flex items-center gap-2 justify-center py-6"
              >
                <Mail className="w-5 h-5" />
                <span>Email</span>
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Tip: Pergi ke tab "Tetapan" untuk ubah tajuk dan ayat perkongsian
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
